// Routing primitives. The app uses pushState on real paths (/invoices). Demos
// come in two shapes, and both must keep their routes inside the demo, or a
// refresh leaves the demo for the real app and its sign-in screen:
//
//  - THE demo at /demo: pushState under a base path, so its routes read
//    /demo/invoices, /demo/quotes/new. demo.html is the page; the static host
//    serves it at /demo and /demo/, and scripts/build-web.mjs sends every
//    deeper /demo/* refresh back to it (see the /demo/ note in index.html).
//  - The trade demos at /demo-<trade>.html, embedded as iframes on the /for/
//    pages: their pathname is a file, so routes live in the hash
//    (/demo-plumbers.html#/invoices).
//
// navigate() dispatches a synthetic popstate so back/forward and programmatic
// navigation share one code path in app.js.
import { isLoadingButton, hrefToAppPath } from "./dom.js";
import { getScheduleIdFromPath } from "./schedule-format.js";
import { getQuoteIdFromPath } from "./quote-format.js";

export const DEMO_BASE = "/demo";

const HASH_DEMO_PATH = /^\/demo(-[a-z0-9-]+)?\.html$/;

// A demo whose routes live in the hash: every /demo-<trade>.html page. (Until
// 2026-09-11 this matched /demo.html alone, so the trade demos pushState'd to
// /invoices and a refresh dropped you on the real app's sign-in.)
export function usesHashRouting() {
  return typeof window !== "undefined" && HASH_DEMO_PATH.test(window.location.pathname);
}

// The /demo base path, when this page is the demo at /demo; "" everywhere else.
// Only on a page actually running the demo (demo-intercept.js sets the flag
// before app.js loads): if the real app's shell were ever served at a /demo
// address, it must not start routing its sign-in screen under /demo.
export function routeBase() {
  if (typeof window === "undefined" || !window.__SENDINVO_DEMO) return "";
  const p = window.location.pathname;
  return p === DEMO_BASE || p.startsWith(`${DEMO_BASE}/`) ? DEMO_BASE : "";
}

// Any demo, however it routes. Gates demo-only screens (the client's pay and
// quote pages) and demo-only behaviour.
export function isDemoPage() {
  return usesHashRouting() || routeBase() !== "";
}

// The address for an app route on this page: "/invoices" is "/invoices" in the
// app, "/demo/invoices" in the demo, and "/demo-plumbers.html#/invoices" in a
// hash-routed demo. Use it anywhere a route becomes a URL (replaceState, a
// shareable link, a hard redirect) rather than writing the path as-is.
export function appUrl(path) {
  const value = String(path || "/");
  if (usesHashRouting()) return `${window.location.pathname}${window.location.search}#${value}`;
  const base = routeBase();
  if (!base) return value;
  return value === "/" ? `${base}/` : `${base}${value}`;
}

export function route() {
  if (usesHashRouting()) {
    return window.location.hash.replace(/^#/, "") || "/";
  }
  // Everywhere else route() reflects the real URL pathname (+ query), less
  // the demo's base, so direct links like /company/payments survive a refresh.
  const base = routeBase();
  const pathname = window.location.pathname || "/";
  const path = base ? pathname.slice(base.length) || "/" : pathname;
  const query = window.location.search || "";
  return path + query;
}

// A demo opened on no route at all — /demo, or /demo-plumbers.html with no
// hash — would render the app's "/" route, which is the marketing landing.
// Start it on the Schedule instead, where the app opens with jobs on, and
// jobs are on in every demo (demo-intercept.js). Runs once, before the first
// render.
export function enterDemoAtHome() {
  if (!isDemoPage()) return;
  if (route().split("?")[0] !== "/") return;
  window.history.replaceState(null, "", appUrl("/schedule"));
}

export function parseRouteState() {
  const [path = "/", query = ""] = String(route() || "/").split("?");
  return {
    path: path || "/",
    query: new URLSearchParams(query)
  };
}

// ── A page for one person in one company ────────────────────────────────────
//
// Every list screen keeps its search, filters and sort at module scope, so a
// re-paint keeps them (screens/jobs.js's listView, invoices.js's, quotes.js's,
// contacts.js's, schedules.js's, team.js's …), and `state` holds the company's
// documents. None of that is anyone else's to see. So the page is loaded
// afresh whenever the person or the company it was built for changes, rather
// than each screen clearing its own (a screen added later would forget):
// Log out and switching company load a new page (`loadFresh`), and a session
// that turns out to be somebody else's, or another company's (signing in as
// someone else after a session ended, accepting an invite into another
// company, a switch made in another tab), makes the next navigation one.

let pageIdentity = null;
let pageStale = false;

/** Who a session is for, as `loadSession` read it: "userId:companyId", or null. */
export function sessionIdentity(user, membership) {
  return user?.id ? `${user.id}:${membership?.companyId || ""}` : null;
}

/**
 * What `loadSession` read. The first person and company a page is signed in
 * as is the one it is for; a different one later means its state is someone
 * else's, and the next `navigate` loads a fresh page.
 */
export function noteSessionIdentity(identity) {
  if (!identity) return;
  if (pageIdentity == null) pageIdentity = identity;
  else if (identity !== pageIdentity) pageStale = true;
}

/** Whether the page holds state for another person or company. */
export function pageIsStale() {
  return pageStale;
}

/**
 * Load `path` as a new page, so nothing in memory outlives it: every
 * module's search, filters and sort, and every document in `state`.
 */
export function loadFresh(path) {
  if (usesHashRouting()) {
    // Setting the hash alone keeps the page; reload it at the new route.
    window.location.hash = path;
    window.location.reload();
  } else {
    window.location.assign(appUrl(path));
  }
}

export function navigate(path) {
  if (pageStale) {
    loadFresh(path);
    return;
  }
  if (route() === path) return;
  const activeButton = document.activeElement;
  if (isLoadingButton(activeButton)) {
    activeButton.classList.add("is-loading");
    activeButton.setAttribute("aria-busy", "true");
    activeButton.disabled = true;
  }
  if (usesHashRouting()) {
    window.location.hash = path;
    return;
  }
  // pushState keeps the real URL in sync; dispatching popstate runs the
  // same render path as back/forward navigation so there's one code path.
  window.history.pushState(null, "", appUrl(path));
  window.dispatchEvent(new PopStateEvent("popstate"));
}

// Draw the current route again from scratch, the way arriving at it does: the
// screen's teardown runs and it re-reads what it shows. navigate() to the path
// already showing is a no-op, which is right for a link and wrong for "open the
// saved version of this draft".
export function reloadRoute() {
  window.dispatchEvent(usesHashRouting() ? new HashChangeEvent("hashchange") : new PopStateEvent("popstate"));
}

export function getInvoiceIdFromPath(path) {
  const match = String(path || "").match(/^\/invoices\/([^/]+)$/);
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

// /team?person=<membership id> — a person, in their drawer over Team. A
// visit's Assigned to opens it; Team takes the query off as it opens them.
export const TEAM_PERSON_PARAM = "person";

export function teamPersonPath(membershipId) {
  return `/team?${TEAM_PERSON_PARAM}=${encodeURIComponent(String(membershipId || ""))}`;
}

// /jobs/:id — a job, in its drawer over the Jobs list (ADR-039). "new" is
// New job's own route, not a job.
export function getJobIdFromPath(path) {
  const match = String(path || "").match(/^\/jobs\/([^/]+)$/);
  if (!match || match[1] === "new") return "";
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

// /pay/:token — the DEMO's client-facing pay page. Only the demo routes here
// (app.js gates the dispatch on isDemoPage()): in production this surface is
// server-rendered by the API at /pay/:token, never by the SPA. Not in
// routeRequiresAuth — it is the payer's page, not the user's.
export function getPayTokenFromPath(path) {
  const match = String(path || "").match(/^\/pay\/([^/]+)$/);
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

// /q/:token — the DEMO's client-facing quote page (accept / decline). Only
// the demo routes here, like /pay/:token above: in production this page is
// server-rendered by the API. Not in routeRequiresAuth — it's the client's
// page, not the user's.
export function getQuoteTokenFromPath(path) {
  const match = String(path || "").match(/^\/q\/([^/]+)$/);
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

// /invite/:token — deliberately NOT added to routeRequiresAuth (below): the
// invite-accept page must stay reachable while logged out (spec §6.3). It's
// the first SPA route with a dynamic path segment that's public rather than
// auth-gated, so app.js's isPublicAuthRoute check has to name it explicitly.
export function getInviteTokenFromPath(path) {
  const match = String(path || "").match(/^\/invite\/([^/]+)$/);
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

export function wireHashButtonLoading() {
  document.addEventListener("click", (evt) => {
    const link = evt.target instanceof Element ? evt.target.closest("a.ar-btn") : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    const path = hrefToAppPath(link.getAttribute("href"));
    if (!path || route() === path) return;
    link.classList.add("is-loading");
    link.setAttribute("aria-busy", "true");
  });
}

// Intercept clicks on in-app <a href="/..."> links so browsers don't trigger
// full page reloads. Modifier-clicks (cmd/ctrl/shift/alt) and target=_blank
// still open in a new tab as normal.
export function wireAppLinkInterception() {
  document.addEventListener("click", (evt) => {
    if (evt.defaultPrevented) return;
    if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;
    if (typeof evt.button === "number" && evt.button !== 0) return;
    const link = evt.target instanceof Element ? evt.target.closest("a") : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    if (link.target && link.target !== "" && link.target !== "_self") return;
    if (link.hasAttribute("download")) return;
    if (link.dataset.noIntercept === "true") return;
    const href = link.getAttribute("href");
    const path = hrefToAppPath(href);
    if (!path) return;
    // /features/* and /for/* are static marketing pages served as real files,
    // *.html are standalone pages, and /demo is its own page — none are routes
    // of THIS page. Let the browser do a full navigation so they actually load,
    // instead of pushState-routing into a screen that doesn't exist. (See plan
    // rule 4: these paths must not collide with SPA routes.)
    if (path.endsWith(".html") || /^\/(features|for|demo)(\/|$)/.test(path)) return;
    // On a hash-routed demo, leave #/ links to the default hashchange
    // behaviour — the browser just changes the hash, which the app listens for.
    if (usesHashRouting() && String(href || "").startsWith("#/")) return;
    evt.preventDefault();
    navigate(path);
  });
}

export function routeRequiresAuth(path) {
  const protectedRoutes = new Set([
    "/company",
    "/invoices",
    "/invoices/new",
    "/quotes",
    "/quotes/new",
    "/schedules",
    "/schedules/new",
    "/contacts",
    "/contacts/add",
    "/jobs",
    "/jobs/new",
    "/schedule",
    "/team",
    "/team/activity",
    "/profile"
  ]);
  if (protectedRoutes.has(path)) return true;
  // Sub-routes under /company and /profile (tabs) are also protected
  if (/^\/company(\/|$)/.test(path)) return true;
  if (/^\/profile(\/|$)/.test(path)) return true;
  return Boolean(getInvoiceIdFromPath(path)) || Boolean(getJobIdFromPath(path)) || Boolean(getQuoteIdFromPath(path)) || Boolean(getScheduleIdFromPath(path)) || path.startsWith("/schedules/");
}

// Where to go once signed in, when signing in was on the way somewhere.
//
// A signed-out visitor who opens an app route from an email (billing help, a
// payment received, an invoice) is sent to /login, and used to land on
// Invoices afterwards whatever the link named. The route now rides along as
// /login?next=…, and across the verify-code step in state.verification.
//
// `next` comes from the address bar, so it's checked before anyone is sent
// there. It must be a path on this origin: not //host, and not /\host, which a
// URL parser reads as //host. And it must be a route that needs signing in,
// which keeps /, /login and the other public routes out: only an app screen
// can come back from here.
const RETURN_BASE = "https://return.invalid";

/**
 * The route to return to after signing in, or null when `value` isn't one.
 *
 * @param {unknown} value  a path and query, e.g. "/profile/billing?web=1"
 * @returns {string|null}  the same path and query, less any fragment
 */
export function safeReturnPath(value) {
  const raw = typeof value === "string" ? value : "";
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  let url;
  try {
    url = new URL(raw, RETURN_BASE);
  } catch {
    return null;
  }
  if (url.origin !== RETURN_BASE) return null;
  if (!routeRequiresAuth(url.pathname)) return null;
  return url.pathname + url.search;
}

/**
 * The sign-in route for a visitor who asked for `requested`: /login, carrying
 * the route to come back to when there is one worth carrying.
 *
 * @param {string} requested  the path and query that needed signing in
 * @returns {string}
 */
export function loginPathFor(requested) {
  const next = safeReturnPath(requested);
  return next ? `/login?next=${encodeURIComponent(next)}` : "/login";
}

// A route that exists only to WRITE something, and the capability the server
// already demands of it — plus where to send someone who hasn't got it
// (rulings 1 and 2, issue #497).
//
// Without this a Viewer could open compose, fill in a whole invoice and find
// out at Save. The server always refused; the screen just never asked.
//
// `/invoices/:id` and `/quotes/:id` are deliberately NOT here. Those paths
// serve the compose screen for a DRAFT and the status drawer for a sent
// document, and a Viewer is meant to reach the drawer — Take payment, Copy
// invoice link, View payment page and Download PDF are all theirs (ruling 3).
// Those two screens decide it themselves, once they know what the document is.
const CAPABILITY_ROUTES = [
  { test: (p) => p === "/invoices/new", capability: "documents.write", fallback: "/invoices" },
  { test: (p) => p === "/quotes/new", capability: "documents.write", fallback: "/quotes" },
  { test: (p) => p === "/schedules/new", capability: "documents.write", fallback: "/schedules" },
  { test: (p) => /^\/schedules\/[^/]+\/edit$/.test(p), capability: "documents.write", fallback: "/schedules" },
  { test: (p) => p === "/contacts/add", capability: "clients.write", fallback: "/contacts" },
  { test: (p) => p === "/jobs/new", capability: "documents.write", fallback: "/jobs" },
  // Accounting (#951) is everyone's to read, as which books the company
  // keeps is: connecting them is `accounting.connect`, asked by its buttons.
  { test: (p) => p === "/company/accounting", capability: "documents.read", fallback: "/invoices" },
  // Subscription (30 October 2026) is everyone's to read who sees the
  // company's documents, so a Member or a Viewer can see why invoices have
  // stopped going out; changing it is billing's, asked by its buttons. Never a
  // Field person's (ADR-041).
  { test: (p) => p === "/company/subscription", capability: "documents.read", fallback: "/invoices" },
  // Every other /company screen exists to save a company setting, the three
  // email template editors and the reminder email steps included.
  { test: (p) => p === "/company" || p.startsWith("/company/"), capability: "company.settings", fallback: "/invoices" }
];

/**
 * The capability a route needs and where to send someone without it, or null
 * when anyone signed in may open it.
 *
 * @param {string} path
 * @returns {{capability: string, fallback: string}|null}
 */
export function routeCapability(path) {
  const route = CAPABILITY_ROUTES.find((r) => r.test(String(path || "")));
  return route ? { capability: route.capability, fallback: route.fallback } : null;
}

// Coming back from Stripe Connect onboarding, from either address
// (issue #516).
//
// The web goes out to `/?stripe_connect=…` and comes back to it. A phone goes
// out to `/stripe/connect/return?result=…`, because Apple's browser session
// closes itself on an https address the app is ASSOCIATED WITH, and an app is
// associated with paths rather than query strings.
//
// Stripe can open the phone's address in a real browser too — an interrupted
// onboarding finished on a laptop — so the web understands both.
//
// @param {string} pathname
// @param {string} search
// @returns {"success"|"refresh"|null}
export function stripeConnectResult(pathname, search) {
  const params = new URLSearchParams(String(search || ""));
  const value = String(pathname || "") === "/stripe/connect/return"
    ? params.get("result")
    : params.get("stripe_connect");
  return value === "success" || value === "refresh" ? value : null;
}

// Valid section ids for /company/<section> and /profile/<tab> sub-routes.
// "payments" merges the former "banking-details" + "online-payments" sections;
// company.js redirects those legacy paths to /company/payments.
export const COMPANY_TABS = ["company-details", "payments", "invoicing", "reminders", "accounting", "visit-hours", "visit-reminders", "reviews"];
// "billing" is gone from Profile (15 October 2026): /profile/billing is sent on
// to /company/subscription by app.js.
export const PROFILE_TABS = ["personal-info", "security"];

export function getCompanyTabFromPath(path) {
  const match = String(path || "").match(/^\/company\/([^/]+)$/);
  if (!match) return null;
  return COMPANY_TABS.includes(match[1]) ? match[1] : null;
}

export function getProfileTabFromPath(path) {
  const match = String(path || "").match(/^\/profile\/([^/]+)$/);
  if (!match) return null;
  return PROFILE_TABS.includes(match[1]) ? match[1] : null;
}
